import axios from 'axios';

// ==========================================
// Global API Configuration
// ==========================================

// IMPORTANT: REMOVED cache-control headers to prevent CORS preflight issues
// The server already handles cache prevention with its own headers
// axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
// axios.defaults.headers.common['Pragma'] = 'no-cache';
// axios.defaults.headers.common['Expires'] = '0';

// Request Interceptor to add timestamp to GET requests
axios.interceptors.request.use((config) => {
  // Add timestamp to bypass browser cache for all GET requests
  if (config.method === 'get') {
    config.params = config.params || {};
    // Add unique timestamp to bypass browser cache
    config.params._t = new Date().getTime();
  }

  // Log requests in development (helpful for debugging accounting data issues)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, config.params || config.data);
  }

  return config;
}, (error) => {
  console.error('[API Request Error]', error);
  return Promise.reject(error);
});

// Response Interceptor for consistent error handling and data freshness
axios.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.data);
    }
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401) {
      console.warn('[Auth] Token expired or invalid');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Log CORS errors specifically
    if (error.message?.includes('CORS')) {
      console.error('[CORS Error]', error.message);
    }

    return Promise.reject(error);
  }
);

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Helper function to ensure fresh data after mutations
export const invalidateAndRefetch = async (refetchFunctions) => {
  // Execute all refetch functions after a mutation
  await Promise.all(refetchFunctions.map(refetch => refetch()));
};

export const authAPI = {
  register: (data) => axios.post(`${API_BASE_URL}/auth/register`, data),
  login: (data) => axios.post(`${API_BASE_URL}/auth/login`, data),
};

// ... (the rest of your api.js remains exactly the same) ...

export const usersAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/users`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/users/post`, data, { headers: getAuthHeader() }),
  updatePassword: (id, data) => axios.put(`${API_BASE_URL}/users/${id}/password`, data, { headers: getAuthHeader() }),
  deactivate: (id) => axios.put(`${API_BASE_URL}/users/${id}/deactivate`, {}, { headers: getAuthHeader() }),
  activate: (id) => axios.put(`${API_BASE_URL}/users/${id}/activate`, {}, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/users/${id}`, { headers: getAuthHeader() }),
};

// ==========================================
// Clients (Customers) & Sales Accounts
// ==========================================

export const clientsAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/clients`, { headers: getAuthHeader() }),
  get: (id) => axios.get(`${API_BASE_URL}/clients/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/clients`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/clients/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/clients/${id}`, { headers: getAuthHeader() }),

  getTransactions: (id, params = {}) =>
    axios.get(`${API_BASE_URL}/sales-accounts/transactions/${id}`, {
      headers: getAuthHeader(),
      params
    }),

  // IMPORTANT: Modified to return fresh data after transaction addition
  addTransaction: async (data) => {
    const response = await axios.post(
      `${API_BASE_URL}/sales-accounts/transactions/${data.client_id}`,
      data,
      { headers: getAuthHeader() }
    );
    // Return the response with the updated balance data
    return response;
  },

  setOpeningBalance: (id, data) =>
    axios.post(
      `${API_BASE_URL}/sales-accounts/transactions/${id}/opening-balance`,
      data,
      { headers: getAuthHeader() }
    ),

  getOpeningBalance: (id) =>
    axios.get(
      `${API_BASE_URL}/sales-accounts/transactions/${id}/opening-balance`,
      { headers: getAuthHeader() }
    ),

  updateOpeningBalance: (id, data) =>
    axios.put(
      `${API_BASE_URL}/sales-accounts/transactions/${id}/opening-balance`,
      data,
      { headers: getAuthHeader() }
    ),

  getTransactionCount: (id) =>
    axios.get(`${API_BASE_URL}/sales-accounts/transactions/${id}/transaction-count`, {
      headers: getAuthHeader(),
    }),

  deleteTransaction: (id) =>
    axios.delete(`${API_BASE_URL}/sales-accounts/transactions/${id}`, {
      headers: getAuthHeader(),
    }),

  // IMPORTANT: Modified to return fresh data after transaction update
  updateTransaction: async (id, data) => {
    const response = await axios.put(`${API_BASE_URL}/sales-accounts/transactions/${id}`, data, {
      headers: getAuthHeader(),
    });
    // Return updated transaction with fresh balances
    return response;
  },

  updateOpeningBalancePaid: (customerId, data) =>
    axios.put(`${API_BASE_URL}/sales-accounts/transactions/${customerId}/opening-balance-paid`, data, {
      headers: getAuthHeader(),
    }),

  getOverallSales: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
      if (filters.toDate) queryParams.append('toDate', filters.toDate);
      if (filters.clientName) queryParams.append('clientName', filters.clientName);
      if (filters.productName) queryParams.append('productName', filters.productName);
      if (filters.branchName) queryParams.append('branchName', filters.branchName);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.exportAll) queryParams.append('exportAll', filters.exportAll);
      if (filters.executiveOfficer) queryParams.append('executiveOfficer', filters.executiveOfficer);

      const url = `${API_BASE_URL}/sales-accounts/transactions/all/overall-sales${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await axios.get(url, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching overall sales:', error);
      throw error.response?.data || error.message;
    }
  },

  // Helper to get client balance summary (for dashboard)
  getBalanceSummary: (id) =>
    axios.get(`${API_BASE_URL}/sales-accounts/transactions/${id}/balance-summary`, {
      headers: getAuthHeader(),
    }),
};

// ==========================================
// Vendors & Purchase Accounts
// ==========================================

export const vendorsAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/vendors/getall`, { headers: getAuthHeader() }),
  get: (id) => axios.get(`${API_BASE_URL}/vendors/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/vendors`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/vendors/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/vendors/${id}`, { headers: getAuthHeader() }),

  getTransactions: (id, params = {}) =>
    axios.get(`${API_BASE_URL}/purchases-accounts/transactions/${id}`, {
      headers: getAuthHeader(),
      params
    }),

  addTransaction: (data) =>
    axios.post(
      `${API_BASE_URL}/purchases-accounts/transactions/${data.client_id}`,
      data,
      { headers: getAuthHeader() }
    ),

  setOpeningBalance: (id, data) =>
    axios.post(
      `${API_BASE_URL}/purchases-accounts/transactions/${id}/opening-balance`,
      data,
      { headers: getAuthHeader() }
    ),

  getTransactionCount: (id) =>
    axios.get(`${API_BASE_URL}/purchases-accounts/transactions/${id}/transaction-count`, {
      headers: getAuthHeader(),
    }),

  getOpeningBalance: (id) =>
    axios.get(
      `${API_BASE_URL}/purchases-accounts/transactions/${id}/opening-balance`,
      { headers: getAuthHeader() }
    ),

  updateOpeningBalance: (id, data) =>
    axios.put(
      `${API_BASE_URL}/purchases-accounts/transactions/${id}/opening-balance`,
      data,
      { headers: getAuthHeader() }
    ),

  deleteTransaction: (id) =>
    axios.delete(`${API_BASE_URL}/purchases-accounts/transactions/${id}`, {
      headers: getAuthHeader(),
    }),

  updateTransaction: (id, data) =>
    axios.put(`${API_BASE_URL}/purchases-accounts/transactions/${id}`, data, {
      headers: getAuthHeader(),
    }),

  updateOpeningBalancePaid: (vendorId, data) =>
    axios.put(`${API_BASE_URL}/purchases-accounts/transactions/${vendorId}/opening-balance-paid`, data, {
      headers: getAuthHeader(),
    }),

  getOverallPurchases: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
      if (filters.toDate) queryParams.append('toDate', filters.toDate);
      if (filters.clientName) queryParams.append('clientName', filters.clientName);
      if (filters.productName) queryParams.append('productName', filters.productName);
      if (filters.branchName) queryParams.append('branchName', filters.branchName);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.exportAll) queryParams.append('exportAll', filters.exportAll);
      if (filters.executiveOfficer) queryParams.append('executiveOfficer', filters.executiveOfficer);

      const url = `${API_BASE_URL}/purchases-accounts/transactions/all/overall-purchases${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching overall purchases:', error);
      throw error.response?.data || error.message;
    }
  },

  // Helper to get vendor balance summary
  getBalanceSummary: (id) =>
    axios.get(`${API_BASE_URL}/purchases-accounts/transactions/${id}/balance-summary`, {
      headers: getAuthHeader(),
    }),
};

// ==========================================
// Sales Management
// ==========================================

export const salesAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/sales`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/sales`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/sales/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/sales/${id}`, { headers: getAuthHeader() }),
  getExecutiveOfficers: () => axios.get(`${API_BASE_URL}/sales/sales-executive`, { headers: getAuthHeader() }),
  getClientsByOfficer: (officerName, params = {}) =>
    axios.get(`${API_BASE_URL}/sales/clients-by-officer/${encodeURIComponent(officerName)}`,
      { headers: getAuthHeader(), params }),
  getCustomerSales: (customerId, startDate, endDate) =>
    axios.get(`${API_BASE_URL}/sales-accounts/transactions/${customerId}/sales-summary`, {
      headers: getAuthHeader(),
      params: { startDate, endDate }
    }),
  getSalesByOfficer: (officerName) =>
    axios.get(`${API_BASE_URL}/sales/transactions-by-officer/${encodeURIComponent(officerName)}`, { headers: getAuthHeader() }),
};

// ==========================================
// Purchase Management
// ==========================================

export const purchasesAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/purchases`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/purchases`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/purchases/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/purchases/${id}`, { headers: getAuthHeader() }),
  getExecutiveOfficers: () => axios.get(`${API_BASE_URL}/purchases/all-officers`, { headers: getAuthHeader() }),
  getSuppliersByOfficer: (officerName, params = {}) =>
    axios.get(`${API_BASE_URL}/purchases/clients-by-officer/${encodeURIComponent(officerName)}`,
      { headers: getAuthHeader(), params }),
  getPurchasesByOfficer: (officerName) =>
    axios.get(`${API_BASE_URL}/purchases/transactions-by-officer/${encodeURIComponent(officerName)}`, { headers: getAuthHeader() }),
};

// ==========================================
// Returns Management
// ==========================================

export const getReturns = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await axios.get(`${API_BASE_URL}/returns?${queryParams}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const returnsAPI = {
  getAll: (params = {}) => axios.get(`${API_BASE_URL}/returns`, { headers: getAuthHeader(), params }),
  getByCustomer: (customerName) => axios.get(`${API_BASE_URL}/returns/sales/customer-list`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/returns`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API_BASE_URL}/returns/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/returns/${id}`, { headers: getAuthHeader() }),
};

// ==========================================
// Inventory Management
// ==========================================

// Products
export const getProducts = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventory/products`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addProduct = async (productData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/inventory/products`, productData, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteProduct = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/inventory/products/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Raw Materials
export const getRawMaterials = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/inventory/raw-materials`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addRawMaterial = async (materialData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/inventory/raw-materials`, materialData, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteRawMaterial = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/inventory/raw-materials/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// ==========================================
// Payments Management
// ==========================================

export const paymentsAPI = {
  getSalesPayments: (params = {}) => axios.get(`${API_BASE_URL}/payments/sales`, { headers: getAuthHeader(), params }),
  getPurchasePayments: (params = {}) => axios.get(`${API_BASE_URL}/payments/purchases`, { headers: getAuthHeader(), params }),
};

// ==========================================
// Administration (Branches, Officers, Accounts)
// ==========================================

// Branches
export const getBranches = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/branches`, { headers: getAuthHeader() });
    return response.data.branches || [];
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addBranch = async (branchData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/branches`, branchData, { headers: getAuthHeader() });
    return response.data.branch;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteBranch = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/branches/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const branchesAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/branches`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/branches`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/branches/${id}`, { headers: getAuthHeader() }),
};

// Executive Officers
export const executiveOfficersAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/executive-officers`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/executive-officers`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/executive-officers/${id}`, { headers: getAuthHeader() }),
};

// Company Accounts
export const getCompanyAccounts = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/company-accounts`, { headers: getAuthHeader() });
    return response.data.accounts || [];
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const addCompanyAccount = async (accountData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/company-accounts`, accountData, { headers: getAuthHeader() });
    return response.data.account;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const deleteCompanyAccount = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/company-accounts/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const companyAccountsAPI = {
  getAll: () => axios.get(`${API_BASE_URL}/company-accounts`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API_BASE_URL}/company-accounts`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API_BASE_URL}/company-accounts/${id}`, { headers: getAuthHeader() }),
};

// ==========================================
// Dashboard & Generic Helpers
// ==========================================

export const dashboardAPI = {
  getStats: () => axios.get(`${API_BASE_URL}/dashboard/stats`, { headers: getAuthHeader() }),
  getTopClients: (month, year) =>
    axios.get(`${API_BASE_URL}/dashboard/top-clients`, {
      headers: getAuthHeader(),
      params: { month, year }
    }),
};

export const reportsAPI = {
  getBalanceSummary: (params = {}) =>
    axios.get(`${API_BASE_URL}/reports/balance-summary`, {
      headers: getAuthHeader(),
      params
    }),
};

export const fetchData = (url) =>
  axios.get(`${API_BASE_URL}${url.replace('/api', '')}`, { headers: getAuthHeader() }).then(res => res.data);

export const postData = (url, data) =>
  axios.post(`${API_BASE_URL}${url.replace('/api', '')}`, data, { headers: getAuthHeader() }).then(res => res.data);

export const deleteData = (url) =>
  axios.delete(`${API_BASE_URL}${url.replace('/api', '')}`, { headers: getAuthHeader() }).then(res => res.data);

// ==========================================
// Utility function to ensure data freshness after mutations
// ==========================================

/**
 * Use this function after creating, updating, or deleting transactions
 * to ensure all related data is refreshed from the server
 * 
 * @example
 * // After adding a transaction:
 * await clientsAPI.addTransaction(data);
 * await refreshAccountingData({
 *   refreshClientList: true,
 *   refreshClientDetails: clientId,
 *   refreshDashboard: true
 * });
 */
export const refreshAccountingData = async (options = {}) => {
  const {
    refreshClientList = false,
    refreshClientDetails = null,
    refreshVendorList = false,
    refreshVendorDetails = null,
    refreshDashboard = false,
    refreshTransactions = false,
    customRefetches = []
  } = options;

  const refreshPromises = [];

  if (refreshClientList) {
    refreshPromises.push(clientsAPI.getAll());
  }

  if (refreshClientDetails) {
    refreshPromises.push(clientsAPI.get(refreshClientDetails));
    refreshPromises.push(clientsAPI.getTransactions(refreshClientDetails));
    refreshPromises.push(clientsAPI.getBalanceSummary(refreshClientDetails));
  }

  if (refreshVendorList) {
    refreshPromises.push(vendorsAPI.getAll());
  }

  if (refreshVendorDetails) {
    refreshPromises.push(vendorsAPI.get(refreshVendorDetails));
    refreshPromises.push(vendorsAPI.getTransactions(refreshVendorDetails));
    refreshPromises.push(vendorsAPI.getBalanceSummary(refreshVendorDetails));
  }

  if (refreshDashboard) {
    refreshPromises.push(dashboardAPI.getStats());
  }

  if (refreshTransactions) {
    refreshPromises.push(salesAPI.getAll());
    refreshPromises.push(purchasesAPI.getAll());
  }

  // Add any custom refetch functions
  if (customRefetches.length) {
    refreshPromises.push(...customRefetches);
  }

  try {
    await Promise.all(refreshPromises);
    console.log('[Refresh] Accounting data refreshed successfully');
  } catch (error) {
    console.error('[Refresh] Error refreshing accounting data:', error);
  }
};

export default axios;